import React, { useMemo, useState, useCallback } from 'react';
import { formatPhoneInput } from '@/lib/utils';
import { Search, CheckCircle2, Undo2, UserPlus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { calendarAPI } from '@/lib/api';

/**
 * Phase 27.30.12 — Dedicated Check-in panel.
 *
 * Big search + big "Mark present" button, designed for one-hand use at
 * the door on the day of the event. Live filters as you type and always
 * shows a running headline "X of Y checked in" so the organizer knows
 * how many people are still expected. Includes:
 *   - Walk-in dialog (name + email + phone → creates registered+walk_in row)
 *   - Undo check-in button on each attended row
 *   - Empty state that guides the user toward walk-ins
 *
 * Only shows registered participants + walk-ins (skips cancelled + waitlist).
 */
export default function CheckInPanel({ event, participants, canManage, onChanged }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null); // assignment_id currently updating
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [walkIn, setWalkIn] = useState({ first_name: '', last_name: '', email: '', phone: '', agency: '' });
  const [walkInBusy, setWalkInBusy] = useState(false);

  // Only registered / attended / walk-in rows are candidates for check-in.
  // Cancelled and waitlist rows are hidden from this view.
  const attendable = useMemo(() => {
    return (participants || []).filter((p) => {
      const reg = (p.registration_status || '').toLowerCase();
      return reg === 'registered' || reg === 'attended';
    });
  }, [participants]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return attendable;
    return attendable.filter((p) => {
      const name = [p.first_name, p.last_name, p.guest_first_name, p.guest_last_name, p.name]
        .filter(Boolean).join(' ').toLowerCase();
      const email = (p.email || p.guest_email || '').toLowerCase();
      const npn = String(p.npn || '');
      const phone = String(p.phone || p.guest_phone || '');
      return (
        name.includes(query) ||
        email.includes(query) ||
        npn.includes(query) ||
        phone.includes(query)
      );
    });
  }, [attendable, q]);

  const attendedCount = useMemo(
    () => attendable.filter((p) => (p.attendance_status || '') === 'attended' || (p.attendance_status || '') === 'walk_in').length,
    [attendable],
  );

  const displayName = (p) => {
    const parts = [p.first_name || p.guest_first_name, p.last_name || p.guest_last_name].filter(Boolean);
    if (parts.length) return parts.join(' ');
    return p.name || p.email || p.guest_email || 'Guest';
  };

  const doCheckIn = useCallback(async (p) => {
    setBusy(p.assignment_id);
    try {
      await calendarAPI.bulkAttendance(event.event_id, 'check_in', [p.assignment_id]);
      toast.success(`${displayName(p)} checked in`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Check-in failed');
    } finally {
      setBusy(null);
    }
  }, [event.event_id, onChanged]);

  const doUndo = useCallback(async (p) => {
    setBusy(p.assignment_id);
    try {
      await calendarAPI.bulkAttendance(event.event_id, 'undo_check_in', [p.assignment_id]);
      toast.success(`Check-in undone for ${displayName(p)}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    } finally {
      setBusy(null);
    }
  }, [event.event_id, onChanged]);

  const submitWalkIn = async () => {
    if (!walkIn.first_name.trim() || !walkIn.last_name.trim()) {
      toast.error('First and last name are required');
      return;
    }
    setWalkInBusy(true);
    try {
      await calendarAPI.addWalkIn(event.event_id, {
        first_name: walkIn.first_name.trim(),
        last_name: walkIn.last_name.trim(),
        email: walkIn.email.trim() || null,
        phone: walkIn.phone.trim() || null,
        agency: walkIn.agency.trim() || null,
      });
      toast.success('Walk-in added and checked in');
      setWalkInOpen(false);
      setWalkIn({ first_name: '', last_name: '', email: '', phone: '', agency: '' });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Walk-in failed');
    } finally {
      setWalkInBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="check-in-panel">
      {/* Headline + walk-in button */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1">
              <div className="text-3xl font-bold text-slate-900" data-testid="checkin-headline">
                {attendedCount} <span className="text-slate-400 text-2xl font-medium">/ {attendable.length}</span>
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-0.5">
                Checked in{attendable.length === 1 ? '' : ' so far'}
              </div>
            </div>
            {canManage && (
              <Button
                variant="outline"
                onClick={() => setWalkInOpen(true)}
                data-testid="checkin-add-walkin-btn"
              >
                <UserPlus className="h-4 w-4 mr-1.5" /> Add walk-in
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card>
        <CardContent className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, NPN, or phone…"
              className="pl-9 h-11 text-base"
              autoFocus
              data-testid="checkin-search-input"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
                data-testid="checkin-search-clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Participant list */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500" data-testid="checkin-empty-state">
              {q ? (
                <>
                  <p className="text-sm mb-3">No participant matches &quot;<b>{q}</b>&quot;.</p>
                  {canManage && (
                    <Button size="sm" variant="outline" onClick={() => setWalkInOpen(true)}>
                      <UserPlus className="h-4 w-4 mr-1.5" /> Add as walk-in
                    </Button>
                  )}
                </>
              ) : attendable.length === 0 ? (
                <p className="text-sm">No registered participants yet.</p>
              ) : (
                <p className="text-sm">All participants filtered out.</p>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((p) => {
                const isAttended = (p.attendance_status || '') === 'attended' || (p.attendance_status || '') === 'walk_in';
                const isBusy = busy === p.assignment_id;
                return (
                  <li
                    key={p.assignment_id}
                    className={`flex items-center gap-3 px-4 py-3 ${isAttended ? 'bg-emerald-50/40' : ''}`}
                    data-testid={`checkin-row-${p.assignment_id}`}
                  >
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold ${
                      isAttended ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {isAttended ? <CheckCircle2 className="h-5 w-5" /> : (displayName(p)?.charAt(0) || '?').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-800 truncate">{displayName(p)}</span>
                        {(p.attendance_status || '') === 'walk_in' && (
                          <Badge variant="outline" className="text-[10px]">Walk-in</Badge>
                        )}
                        {p.guest_email === null && p.email === null && !p.guest_first_name && (
                          <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-200">No email</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {p.email || p.guest_email || p.npn || p.phone || p.guest_phone || '—'}
                      </div>
                    </div>
                    {canManage && (
                      isAttended ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={isBusy}
                          onClick={() => doUndo(p)}
                          data-testid={`checkin-undo-btn-${p.assignment_id}`}
                          className="text-slate-500 hover:text-rose-600"
                        >
                          {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Undo2 className="h-4 w-4 mr-1" /> Undo</>}
                        </Button>
                      ) : (
                        <Button
                          size="lg"
                          disabled={isBusy}
                          onClick={() => doCheckIn(p)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 h-11"
                          data-testid={`checkin-mark-present-btn-${p.assignment_id}`}
                        >
                          {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-5 w-5 mr-1.5" /> Mark present</>}
                        </Button>
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Walk-in dialog */}
      <Dialog open={walkInOpen} onOpenChange={setWalkInOpen}>
        <DialogContent data-testid="checkin-walkin-dialog">
          <DialogHeader>
            <DialogTitle>Add walk-in</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-1">
              <Label htmlFor="wi-first">First name *</Label>
              <Input
                id="wi-first"
                value={walkIn.first_name}
                onChange={(e) => setWalkIn((s) => ({ ...s, first_name: e.target.value }))}
                autoFocus
                data-testid="walkin-first-name"
              />
            </div>
            <div className="col-span-1">
              <Label htmlFor="wi-last">Last name *</Label>
              <Input
                id="wi-last"
                value={walkIn.last_name}
                onChange={(e) => setWalkIn((s) => ({ ...s, last_name: e.target.value }))}
                data-testid="walkin-last-name"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="wi-email">Email</Label>
              <Input
                id="wi-email"
                type="email"
                value={walkIn.email}
                onChange={(e) => setWalkIn((s) => ({ ...s, email: e.target.value }))}
                data-testid="walkin-email"
              />
            </div>
            <div className="col-span-1">
              <Label htmlFor="wi-phone">Phone</Label>
              <Input
                id="wi-phone"
                value={formatPhoneInput(walkIn.phone)}
                onChange={(e) => setWalkIn((s) => ({ ...s, phone: e.target.value }))}
                data-testid="walkin-phone"
              />
            </div>
            <div className="col-span-1">
              <Label htmlFor="wi-agency">Agency</Label>
              <Input
                id="wi-agency"
                value={walkIn.agency}
                onChange={(e) => setWalkIn((s) => ({ ...s, agency: e.target.value }))}
                data-testid="walkin-agency"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWalkInOpen(false)} disabled={walkInBusy}>Cancel</Button>
            <Button
              onClick={submitWalkIn}
              disabled={walkInBusy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="walkin-submit"
            >
              {walkInBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
              Add &amp; check in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
